import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/Button';
import { Eyebrow, Display } from '@/components/ui/Typography';
import { friendlyMessage } from '@/lib/errors';

/**
 * Confirmação de troca de email — /confirm-email?token=...
 *
 * Rota pública (o link chega no email NOVO; o usuário pode nem estar logado).
 * A confirmação exige clique explícito — NUNCA auto-POST no mount: scanners
 * de email corporativos renderizam o link em headless browser e consumiriam
 * o token (derrubando todas as sessões do usuário sem ação dele).
 *
 * Pós-confirmação o backend revoga todas as sessões (token_version bump);
 * deslogamos localmente e mandamos pro login.
 */
export default function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  async function onConfirm() {
    setApiError(null);
    setSubmitting(true);
    try {
      await authApi.confirmEmail(token);
      // Sessões antigas foram revogadas no backend — limpa a local também.
      logout();
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setApiError(friendlyMessage(err, 'Não foi possível confirmar o email.'));
    } finally {
      setSubmitting(false);
    }
  }

  const isTokenMissing = !token;

  return (
    <div
      className="mx-auto flex h-dvh max-w-sm flex-col overflow-hidden px-7"
      style={{
        paddingTop: 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1.75rem, env(safe-area-inset-bottom))'
      }}
    >
      <div className="mb-8 flex flex-shrink-0 items-center [@media(max-height:740px)]:mb-5">
        <img src="/logo/logo-bow-01.png" alt="Bow" className="h-9 w-auto" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <Eyebrow>Troca de email</Eyebrow>
        <Display size={36} className="mt-3 [@media(max-height:740px)]:!text-[28px]">
          Confirmar
          <br />
          <span className="text-bow-green">novo email</span>
        </Display>

        {isTokenMissing ? (
          <>
            <div className="min-h-4 flex-1" />
            <div className="flex flex-col gap-4">
              <div className="rounded-3xl bg-bow-red/10 p-5">
                <p className="text-sm font-semibold text-bow-red">Link inválido</p>
                <p className="mt-1 text-sm leading-relaxed text-bow-navy/70">
                  Este link de confirmação é inválido. Peça a troca de email
                  novamente no seu perfil.
                </p>
              </div>
              <Button onClick={() => navigate('/login')}>Ir para o login</Button>
            </div>
          </>
        ) : done ? (
          <>
            <div className="min-h-4 flex-1" />
            <div className="flex flex-col gap-4">
              <div className="rounded-3xl bg-bow-green/10 p-5">
                <p className="text-sm font-semibold text-bow-navy">Email atualizado!</p>
                <p className="mt-1 text-sm leading-relaxed text-bow-navy/70">
                  Sua conta agora usa o novo email. Por segurança, entre
                  novamente. Redirecionando para o login…
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 pt-5">
            <p className="text-sm leading-relaxed text-bow-royal/60">
              Toque no botão abaixo para confirmar este endereço como o novo
              email da sua conta Bow. Por segurança, você precisará entrar de
              novo no app depois da troca.
            </p>

            {apiError && (
              <div className="rounded-2xl bg-bow-red/10 p-3">
                <p className="text-sm text-bow-red">{apiError}</p>
                <p className="mt-1 text-sm text-bow-navy/70">
                  Se o link expirou, peça a troca de email novamente no perfil.
                </p>
              </div>
            )}

            <div className="min-h-2 flex-1" />
            <Button onClick={onConfirm} disabled={submitting}>
              {submitting ? 'Confirmando…' : 'Confirmar novo email'}
            </Button>

            <p className="text-center text-[13px] text-bow-royal/55">
              <Link to="/login" className="font-semibold text-bow-royal">
                Voltar ao login
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
